import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  User, 
  FileSpreadsheet, 
  Check, 
  Building2,
  Store,
  AlertTriangle,
  DollarSign,
  Percent,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  Clock
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import TimeIntegrityDashboard from './TimeIntegrityDashboard';

// Helper to format percentage for display
const formatPercent = (decimal) => decimal != null ? `${(decimal * 100).toFixed(0)}%` : '-';

// Helper to parse percentage input back to decimal
const parsePercent = (percentStr) => {
  const num = parseFloat(percentStr);
  return isNaN(num) ? null : num / 100;
};

// Source badge component
const SourceBadge = ({ source }) => {
  const colors = {
    system: 'bg-gray-100 text-gray-600',
    org: 'bg-blue-100 text-blue-700',
    outlet: 'bg-emerald-100 text-emerald-700'
  };
  const labels = {
    system: 'System Default',
    org: 'Org Override',
    outlet: 'Outlet Override'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[source] || colors.system}`}>
      {labels[source] || source}
    </span>
  );
};

// Threshold input group
const ThresholdInputGroup = ({ label, value, onChange, source, disabled, error }) => {
  const [inputValue, setInputValue] = useState('');
  
  useEffect(() => {
    setInputValue(value != null ? (value * 100).toString() : '');
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (val === '') {
      onChange(null);
    } else {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        onChange(num / 100);
      }
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {source && <SourceBadge source={source} />}
      </div>
      <div className="relative">
        <input
          type="number"
          step="1"
          min="1"
          max="100"
          value={inputValue}
          onChange={handleChange}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-8 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${error ? 'border-red-300' : 'border-gray-300'}`}
          placeholder="Enter %"
          data-testid={`threshold-${label.toLowerCase()}`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

// Currency selector component
const CurrencySelector = ({ value, onChange, supportedCurrencies, symbols, source, disabled }) => {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Base Currency</label>
        {source && <SourceBadge source={source} />}
      </div>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        data-testid="currency-selector"
      >
        <option value="">Use inherited setting</option>
        {supportedCurrencies?.map(code => (
          <option key={code} value={code}>
            {code} ({symbols?.[code] || code})
          </option>
        ))}
      </select>
    </div>
  );
};

const Configuration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('user'); // 'user', 'org', 'outlet', 'time-integrity'
  const [searchParams] = useSearchParams();
  
  // Read tab from URL params (for Intelligence Hub click-through)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['user', 'org', 'outlet', 'time-integrity'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  // System defaults
  const [defaults, setDefaults] = useState(null);
  
  // User Preferences
  const [userPreferences, setUserPreferences] = useState({
    auto_apply_templates_on_perfect_match: false
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);

  // Org Settings
  const [orgSettings, setOrgSettings] = useState(null);
  const [orgEdits, setOrgEdits] = useState({
    drift_thresholds: { warning: null, high: null, critical: null },
    currency: { base_currency: null, symbol: null, decimal_precision: null }
  });
  const [orgSaved, setOrgSaved] = useState(false);
  const [orgError, setOrgError] = useState(null);

  // Outlet Settings
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outletSettings, setOutletSettings] = useState(null);
  const [outletEdits, setOutletEdits] = useState({
    drift_thresholds: { warning: null, high: null, critical: null },
    currency: { base_currency: null, symbol: null, decimal_precision: null }
  });
  const [outletSaved, setOutletSaved] = useState(false);
  const [outletError, setOutletError] = useState(null);

  // Resolved settings for preview
  const [resolvedSettings, setResolvedSettings] = useState(null);

  // Legacy engine configs
  const [engines, setEngines] = useState({});
  const [configs, setConfigs] = useState([]);
  const [selectedEngine, setSelectedEngine] = useState('');
  const [editedConfigs, setEditedConfigs] = useState({});

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    thresholds: true,
    currency: true,
    engines: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load initial data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        defaultsRes,
        prefsRes,
        orgRes,
        outletsRes,
        enginesRes,
        configsRes
      ] = await Promise.all([
        api.getSettingsDefaults(),
        api.getUserPreferences(),
        api.getOrgSettings(),
        api.getOutlets(),
        api.getEngineDefaults(),
        api.getEngineConfigs(),
      ]);

      setDefaults(defaultsRes.data);
      setUserPreferences(prefsRes.data);
      setOrgSettings(orgRes.data);
      
      // Handle outlets - they come as array directly
      const outletList = Array.isArray(outletsRes.data) ? outletsRes.data : (outletsRes.data?.outlets || []);
      setOutlets(outletList);
      
      // Initialize org edits from saved settings
      if (orgRes.data?.drift_thresholds) {
        setOrgEdits(prev => ({
          ...prev,
          drift_thresholds: {
            warning: orgRes.data.drift_thresholds.warning,
            high: orgRes.data.drift_thresholds.high,
            critical: orgRes.data.drift_thresholds.critical
          }
        }));
      }
      if (orgRes.data?.currency) {
        setOrgEdits(prev => ({
          ...prev,
          currency: {
            base_currency: orgRes.data.currency.base_currency,
            symbol: orgRes.data.currency.symbol,
            decimal_precision: orgRes.data.currency.decimal_precision
          }
        }));
      }
      
      // Load resolved settings
      const resolvedRes = await api.getResolvedSettings();
      setResolvedSettings(resolvedRes.data);

      // Legacy engine configs
      setEngines(enginesRes.data);
      setConfigs(configsRes.data);
      if (Object.keys(enginesRes.data).length > 0 && !selectedEngine) {
        setSelectedEngine(Object.keys(enginesRes.data)[0]);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load outlet settings when selected
  useEffect(() => {
    if (selectedOutlet) {
      loadOutletSettings(selectedOutlet);
    }
  }, [selectedOutlet]);

  const loadOutletSettings = async (outletId) => {
    try {
      const [settingsRes, resolvedRes] = await Promise.all([
        api.getOutletSettings(outletId),
        api.getResolvedSettings(outletId)
      ]);
      setOutletSettings(settingsRes.data);
      setResolvedSettings(resolvedRes.data);
      
      // Initialize outlet edits
      if (settingsRes.data?.drift_thresholds) {
        setOutletEdits(prev => ({
          ...prev,
          drift_thresholds: {
            warning: settingsRes.data.drift_thresholds.warning,
            high: settingsRes.data.drift_thresholds.high,
            critical: settingsRes.data.drift_thresholds.critical
          }
        }));
      } else {
        setOutletEdits(prev => ({
          ...prev,
          drift_thresholds: { warning: null, high: null, critical: null }
        }));
      }
      if (settingsRes.data?.currency) {
        setOutletEdits(prev => ({
          ...prev,
          currency: {
            base_currency: settingsRes.data.currency.base_currency,
            symbol: settingsRes.data.currency.symbol,
            decimal_precision: settingsRes.data.currency.decimal_precision
          }
        }));
      } else {
        setOutletEdits(prev => ({
          ...prev,
          currency: { base_currency: null, symbol: null, decimal_precision: null }
        }));
      }
    } catch (error) {
      console.error('Error loading outlet settings:', error);
    }
  };

  // User preferences handlers
  const handlePreferenceChange = async (key, value) => {
    setSavingPrefs(true);
    setPrefsSaved(false);
    try {
      const response = await api.updateUserPreferences({ [key]: value });
      setUserPreferences(response.data);
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    } catch (error) {
      console.error('Error updating preference:', error);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Validate thresholds
  const validateThresholds = (thresholds) => {
    const { warning, high, critical } = thresholds;
    const errors = {};
    
    if (warning != null && high != null && warning >= high) {
      errors.warning = 'Must be less than High';
      errors.high = 'Must be greater than Warning';
    }
    if (high != null && critical != null && high >= critical) {
      errors.high = errors.high || 'Must be less than Critical';
      errors.critical = 'Must be greater than High';
    }
    
    return errors;
  };

  // Save org settings
  const handleSaveOrgSettings = async () => {
    const errors = validateThresholds(orgEdits.drift_thresholds);
    if (Object.keys(errors).length > 0) {
      setOrgError('Thresholds must be in order: Warning < High < Critical');
      return;
    }
    
    setSaving(true);
    setOrgError(null);
    try {
      const payload = {};
      
      // Only include thresholds if any value is set
      const hasThresholds = orgEdits.drift_thresholds.warning != null || 
                           orgEdits.drift_thresholds.high != null || 
                           orgEdits.drift_thresholds.critical != null;
      if (hasThresholds) {
        payload.drift_thresholds = orgEdits.drift_thresholds;
      }
      
      // Only include currency if any value is set
      const hasCurrency = orgEdits.currency.base_currency != null;
      if (hasCurrency) {
        payload.currency = orgEdits.currency;
      }
      
      await api.updateOrgSettings(payload);
      
      // Reload data
      const [orgRes, resolvedRes] = await Promise.all([
        api.getOrgSettings(),
        api.getResolvedSettings(selectedOutlet)
      ]);
      setOrgSettings(orgRes.data);
      setResolvedSettings(resolvedRes.data);
      
      setOrgSaved(true);
      setTimeout(() => setOrgSaved(false), 2000);
    } catch (error) {
      console.error('Error saving org settings:', error);
      setOrgError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset org setting
  const handleResetOrgSetting = async (settingKey) => {
    setSaving(true);
    try {
      await api.resetOrgSetting(settingKey);
      
      // Clear local edits
      if (settingKey === 'drift_thresholds') {
        setOrgEdits(prev => ({
          ...prev,
          drift_thresholds: { warning: null, high: null, critical: null }
        }));
      } else if (settingKey === 'currency') {
        setOrgEdits(prev => ({
          ...prev,
          currency: { base_currency: null, symbol: null, decimal_precision: null }
        }));
      }
      
      // Reload data
      const [orgRes, resolvedRes] = await Promise.all([
        api.getOrgSettings(),
        api.getResolvedSettings(selectedOutlet)
      ]);
      setOrgSettings(orgRes.data);
      setResolvedSettings(resolvedRes.data);
      
      setOrgSaved(true);
      setTimeout(() => setOrgSaved(false), 2000);
    } catch (error) {
      console.error('Error resetting org setting:', error);
    } finally {
      setSaving(false);
    }
  };

  // Save outlet settings
  const handleSaveOutletSettings = async () => {
    if (!selectedOutlet) return;
    
    const errors = validateThresholds(outletEdits.drift_thresholds);
    if (Object.keys(errors).length > 0) {
      setOutletError('Thresholds must be in order: Warning < High < Critical');
      return;
    }
    
    setSaving(true);
    setOutletError(null);
    try {
      const payload = {};
      
      const hasThresholds = outletEdits.drift_thresholds.warning != null || 
                           outletEdits.drift_thresholds.high != null || 
                           outletEdits.drift_thresholds.critical != null;
      if (hasThresholds) {
        payload.drift_thresholds = outletEdits.drift_thresholds;
      }
      
      const hasCurrency = outletEdits.currency.base_currency != null;
      if (hasCurrency) {
        payload.currency = outletEdits.currency;
      }
      
      await api.updateOutletSettings(selectedOutlet, payload);
      
      const [settingsRes, resolvedRes] = await Promise.all([
        api.getOutletSettings(selectedOutlet),
        api.getResolvedSettings(selectedOutlet)
      ]);
      setOutletSettings(settingsRes.data);
      setResolvedSettings(resolvedRes.data);
      
      setOutletSaved(true);
      setTimeout(() => setOutletSaved(false), 2000);
    } catch (error) {
      console.error('Error saving outlet settings:', error);
      setOutletError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset outlet setting
  const handleResetOutletSetting = async (settingKey) => {
    if (!selectedOutlet) return;
    
    setSaving(true);
    try {
      await api.resetOutletSetting(selectedOutlet, settingKey);
      
      if (settingKey === 'drift_thresholds') {
        setOutletEdits(prev => ({
          ...prev,
          drift_thresholds: { warning: null, high: null, critical: null }
        }));
      } else if (settingKey === 'currency') {
        setOutletEdits(prev => ({
          ...prev,
          currency: { base_currency: null, symbol: null, decimal_precision: null }
        }));
      }
      
      const [settingsRes, resolvedRes] = await Promise.all([
        api.getOutletSettings(selectedOutlet),
        api.getResolvedSettings(selectedOutlet)
      ]);
      setOutletSettings(settingsRes.data);
      setResolvedSettings(resolvedRes.data);
      
      setOutletSaved(true);
      setTimeout(() => setOutletSaved(false), 2000);
    } catch (error) {
      console.error('Error resetting outlet setting:', error);
    } finally {
      setSaving(false);
    }
  };

  // Legacy engine config helpers
  const getConfigValue = (engineName, configKey) => {
    const editKey = `${engineName}.${configKey}`;
    if (editedConfigs[editKey] !== undefined) {
      return editedConfigs[editKey];
    }
    const config = configs.find(
      (c) => c.engine_name === engineName && c.config_key === configKey
    );
    if (config) {
      return config.config_value;
    }
    return engines[engineName]?.[configKey]?.default || '';
  };

  const getConfigId = (engineName, configKey) => {
    const config = configs.find(
      (c) => c.engine_name === engineName && c.config_key === configKey
    );
    return config?.config_id;
  };

  const handleConfigChange = (engineName, configKey, value) => {
    const editKey = `${engineName}.${configKey}`;
    setEditedConfigs({
      ...editedConfigs,
      [editKey]: value,
    });
  };

  const handleSaveEngineConfigs = async () => {
    setSaving(true);
    try {
      const updates = [];
      for (const [editKey, value] of Object.entries(editedConfigs)) {
        const [engineName, configKey] = editKey.split('.');
        const configId = getConfigId(engineName, configKey);
        if (configId) {
          updates.push(
            api.updateEngineConfig(configId, {
              config_value: value,
              updated_by: 'ADMIN',
            })
          );
        }
      }
      await Promise.all(updates);
      setEditedConfigs({});
      loadAllData();
    } catch (error) {
      console.error('Error saving configurations:', error);
    } finally {
      setSaving(false);
    }
  };

  const initializeConfigs = async () => {
    try {
      await api.initializeEngineConfigs();
      loadAllData();
    } catch (error) {
      console.error('Error initializing configs:', error);
    }
  };

  const hasEngineChanges = Object.keys(editedConfigs).length > 0;

  const getEngineIcon = (engineName) => {
    const icons = {
      BILL_ENGINE: '📄',
      INVENTORY_ENGINE: '📦',
      SALES_ENGINE: '💰',
      ACCOUNTS_ENGINE: '💳',
      EMPLOYEE_ENGINE: '👥',
    };
    return icons[engineName] || '⚙️';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="configuration-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuration Management</h1>
          <p className="text-gray-600 mt-2">Manage settings for your organization and outlets</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 max-w-lg">
        <button
          onClick={() => setActiveTab('user')}
          data-testid="tab-user"
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'user' 
              ? 'bg-white text-blue-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User size={16} />
          User
        </button>
        <button
          onClick={() => setActiveTab('org')}
          data-testid="tab-org"
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'org' 
              ? 'bg-white text-blue-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Building2 size={16} />
          Organization
        </button>
        <button
          onClick={() => setActiveTab('outlet')}
          data-testid="tab-outlet"
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'outlet' 
              ? 'bg-white text-blue-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Store size={16} />
          Outlet
        </button>
        <button
          onClick={() => setActiveTab('time-integrity')}
          data-testid="tab-time-integrity"
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'time-integrity' 
              ? 'bg-white text-blue-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock size={16} />
          Time Integrity
        </button>
      </div>

      {/* User Preferences Tab */}
      {activeTab === 'user' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="user-preferences-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <User className="text-indigo-600" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">User Preferences</h2>
                <p className="text-sm text-gray-500">Personal settings for your account</p>
              </div>
              {prefsSaved && (
                <div className="ml-auto flex items-center gap-1 text-green-600 text-sm">
                  <Check size={16} />
                  Saved
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="text-gray-600 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Auto-apply mapping templates on perfect match</p>
                    <p className="text-sm text-gray-500 mt-1">
                      When uploading Excel files, automatically apply saved templates that match 100% of your headers.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={userPreferences.auto_apply_templates_on_perfect_match || false}
                    onChange={(e) => handlePreferenceChange('auto_apply_templates_on_perfect_match', e.target.checked)}
                    disabled={savingPrefs}
                    className="sr-only peer"
                    data-testid="auto-apply-templates-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Settings Tab */}
      {activeTab === 'org' && (
        <div className="space-y-6">
          {/* Resolved Settings Preview */}
          {resolvedSettings && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Info size={18} className="text-blue-600" />
                <h3 className="font-semibold text-blue-900">Current Effective Settings</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-700 mb-2">Drift Thresholds</p>
                  <div className="flex gap-3">
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-200">
                      <span className="text-xs text-gray-500 block">Warning</span>
                      <span className="text-lg font-semibold text-amber-600">{formatPercent(resolvedSettings.drift_thresholds?.warning)}</span>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-200">
                      <span className="text-xs text-gray-500 block">High</span>
                      <span className="text-lg font-semibold text-orange-600">{formatPercent(resolvedSettings.drift_thresholds?.high)}</span>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-200">
                      <span className="text-xs text-gray-500 block">Critical</span>
                      <span className="text-lg font-semibold text-red-600">{formatPercent(resolvedSettings.drift_thresholds?.critical)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Source: <SourceBadge source={resolvedSettings.sources?.drift_thresholds} />
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-700 mb-2">Currency</p>
                  <div className="bg-white rounded-lg px-4 py-2 border border-blue-200 inline-block">
                    <span className="text-2xl mr-2">{resolvedSettings.currency?.symbol}</span>
                    <span className="font-semibold">{resolvedSettings.currency?.base_currency}</span>
                    <span className="text-sm text-gray-500 ml-2">({resolvedSettings.currency?.decimal_precision} decimals)</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Source: <SourceBadge source={resolvedSettings.sources?.currency} />
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Drift Thresholds Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('thresholds')}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="text-amber-600" size={20} />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900">Price Drift Thresholds</h2>
                  <p className="text-sm text-gray-500">Configure alert sensitivity for price deviations</p>
                </div>
              </div>
              {expandedSections.thresholds ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSections.thresholds && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                {orgError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {orgError}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <ThresholdInputGroup
                    label="Warning"
                    value={orgEdits.drift_thresholds.warning}
                    onChange={(val) => setOrgEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, warning: val }
                    }))}
                    source={orgSettings?.drift_thresholds?.warning != null ? 'org' : null}
                    disabled={saving}
                    error={validateThresholds(orgEdits.drift_thresholds).warning}
                  />
                  <ThresholdInputGroup
                    label="High"
                    value={orgEdits.drift_thresholds.high}
                    onChange={(val) => setOrgEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, high: val }
                    }))}
                    source={orgSettings?.drift_thresholds?.high != null ? 'org' : null}
                    disabled={saving}
                    error={validateThresholds(orgEdits.drift_thresholds).high}
                  />
                  <ThresholdInputGroup
                    label="Critical"
                    value={orgEdits.drift_thresholds.critical}
                    onChange={(val) => setOrgEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, critical: val }
                    }))}
                    source={orgSettings?.drift_thresholds?.critical != null ? 'org' : null}
                    disabled={saving}
                    error={validateThresholds(orgEdits.drift_thresholds).critical}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Leave empty to use system defaults ({formatPercent(defaults?.drift_thresholds?.warning)} / {formatPercent(defaults?.drift_thresholds?.high)} / {formatPercent(defaults?.drift_thresholds?.critical)})
                  </p>
                  <div className="flex gap-2">
                    {orgSettings?.drift_thresholds && (
                      <button
                        onClick={() => handleResetOrgSetting('drift_thresholds')}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        data-testid="reset-org-thresholds"
                      >
                        <RotateCcw size={14} />
                        Reset to Default
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Currency Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('currency')}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-emerald-600" size={20} />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900">Currency Settings</h2>
                  <p className="text-sm text-gray-500">Configure base currency for pricing and reports</p>
                </div>
              </div>
              {expandedSections.currency ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSections.currency && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <CurrencySelector
                    value={orgEdits.currency.base_currency}
                    onChange={(val) => {
                      const symbol = defaults?.currency_symbols?.[val] || val;
                      setOrgEdits(prev => ({
                        ...prev,
                        currency: { 
                          ...prev.currency, 
                          base_currency: val,
                          symbol: symbol,
                          decimal_precision: 2
                        }
                      }));
                    }}
                    supportedCurrencies={defaults?.supported_currencies}
                    symbols={defaults?.currency_symbols}
                    source={orgSettings?.currency?.base_currency ? 'org' : null}
                    disabled={saving}
                  />
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Symbol</label>
                    <input
                      type="text"
                      value={orgEdits.currency.symbol || ''}
                      onChange={(e) => setOrgEdits(prev => ({
                        ...prev,
                        currency: { ...prev.currency, symbol: e.target.value || null }
                      }))}
                      disabled={saving || !orgEdits.currency.base_currency}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      placeholder="Auto"
                      data-testid="currency-symbol"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Decimal Precision</label>
                    <select
                      value={orgEdits.currency.decimal_precision ?? ''}
                      onChange={(e) => setOrgEdits(prev => ({
                        ...prev,
                        currency: { ...prev.currency, decimal_precision: e.target.value ? parseInt(e.target.value) : null }
                      }))}
                      disabled={saving || !orgEdits.currency.base_currency}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      data-testid="currency-precision"
                    >
                      <option value="">Default (2)</option>
                      <option value="0">0 decimals</option>
                      <option value="1">1 decimal</option>
                      <option value="2">2 decimals</option>
                      <option value="3">3 decimals</option>
                      <option value="4">4 decimals</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Default: {defaults?.currency?.base_currency} ({defaults?.currency?.symbol})
                  </p>
                  {orgSettings?.currency && (
                    <button
                      onClick={() => handleResetOrgSetting('currency')}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      data-testid="reset-org-currency"
                    >
                      <RotateCcw size={14} />
                      Reset to Default
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            {orgSaved && (
              <div className="flex items-center gap-1 text-green-600 text-sm mr-4">
                <Check size={16} />
                Settings saved
              </div>
            )}
            <button
              onClick={handleSaveOrgSettings}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              data-testid="save-org-settings"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Save Organization Settings
            </button>
          </div>
        </div>
      )}

      {/* Outlet Settings Tab */}
      {activeTab === 'outlet' && (
        <div className="space-y-6">
          {/* Outlet Selector */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Store className="text-purple-600" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Select Outlet</h2>
                <p className="text-sm text-gray-500">Override organization settings for a specific outlet</p>
              </div>
            </div>
            
            <select
              value={selectedOutlet || ''}
              onChange={(e) => setSelectedOutlet(e.target.value || null)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="outlet-selector"
            >
              <option value="">Choose an outlet...</option>
              {outlets.map(outlet => (
                <option key={outlet.outlet_id} value={outlet.outlet_id}>
                  {outlet.outlet_name || outlet.name}
                </option>
              ))}
            </select>
          </div>

          {selectedOutlet && (
            <>
              {/* Resolved Settings Preview for Outlet */}
              {resolvedSettings && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Info size={18} className="text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Effective Settings for This Outlet</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-purple-700 mb-2">Drift Thresholds</p>
                      <div className="flex gap-3">
                        <div className="bg-white rounded-lg px-3 py-2 border border-purple-200">
                          <span className="text-xs text-gray-500 block">Warning</span>
                          <span className="text-lg font-semibold text-amber-600">{formatPercent(resolvedSettings.drift_thresholds?.warning)}</span>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2 border border-purple-200">
                          <span className="text-xs text-gray-500 block">High</span>
                          <span className="text-lg font-semibold text-orange-600">{formatPercent(resolvedSettings.drift_thresholds?.high)}</span>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2 border border-purple-200">
                          <span className="text-xs text-gray-500 block">Critical</span>
                          <span className="text-lg font-semibold text-red-600">{formatPercent(resolvedSettings.drift_thresholds?.critical)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">
                        Source: <SourceBadge source={resolvedSettings.sources?.drift_thresholds} />
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-700 mb-2">Currency</p>
                      <div className="bg-white rounded-lg px-4 py-2 border border-purple-200 inline-block">
                        <span className="text-2xl mr-2">{resolvedSettings.currency?.symbol}</span>
                        <span className="font-semibold">{resolvedSettings.currency?.base_currency}</span>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">
                        Source: <SourceBadge source={resolvedSettings.sources?.currency} />
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Outlet Drift Thresholds */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <AlertTriangle className="text-amber-600" size={20} />
                  <h3 className="font-semibold text-gray-900">Outlet Threshold Overrides</h3>
                  {outletSettings?.drift_thresholds && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      Override Active
                    </span>
                  )}
                </div>
                
                {outletError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {outletError}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <ThresholdInputGroup
                    label="Warning"
                    value={outletEdits.drift_thresholds.warning}
                    onChange={(val) => setOutletEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, warning: val }
                    }))}
                    source={outletSettings?.drift_thresholds?.warning != null ? 'outlet' : null}
                    disabled={saving}
                    error={validateThresholds(outletEdits.drift_thresholds).warning}
                  />
                  <ThresholdInputGroup
                    label="High"
                    value={outletEdits.drift_thresholds.high}
                    onChange={(val) => setOutletEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, high: val }
                    }))}
                    source={outletSettings?.drift_thresholds?.high != null ? 'outlet' : null}
                    disabled={saving}
                    error={validateThresholds(outletEdits.drift_thresholds).high}
                  />
                  <ThresholdInputGroup
                    label="Critical"
                    value={outletEdits.drift_thresholds.critical}
                    onChange={(val) => setOutletEdits(prev => ({
                      ...prev,
                      drift_thresholds: { ...prev.drift_thresholds, critical: val }
                    }))}
                    source={outletSettings?.drift_thresholds?.critical != null ? 'outlet' : null}
                    disabled={saving}
                    error={validateThresholds(outletEdits.drift_thresholds).critical}
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    Leave empty to inherit from organization or system defaults
                  </p>
                  {outletSettings?.drift_thresholds && (
                    <button
                      onClick={() => handleResetOutletSetting('drift_thresholds')}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      data-testid="reset-outlet-thresholds"
                    >
                      <RotateCcw size={14} />
                      Remove Override
                    </button>
                  )}
                </div>
              </div>

              {/* Outlet Currency */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="text-emerald-600" size={20} />
                  <h3 className="font-semibold text-gray-900">Outlet Currency Override</h3>
                  {outletSettings?.currency && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      Override Active
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <CurrencySelector
                    value={outletEdits.currency.base_currency}
                    onChange={(val) => {
                      const symbol = defaults?.currency_symbols?.[val] || val;
                      setOutletEdits(prev => ({
                        ...prev,
                        currency: { 
                          ...prev.currency, 
                          base_currency: val,
                          symbol: symbol,
                          decimal_precision: 2
                        }
                      }));
                    }}
                    supportedCurrencies={defaults?.supported_currencies}
                    symbols={defaults?.currency_symbols}
                    source={outletSettings?.currency?.base_currency ? 'outlet' : null}
                    disabled={saving}
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    Leave empty to inherit from organization
                  </p>
                  {outletSettings?.currency && (
                    <button
                      onClick={() => handleResetOutletSetting('currency')}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      data-testid="reset-outlet-currency"
                    >
                      <RotateCcw size={14} />
                      Remove Override
                    </button>
                  )}
                </div>
              </div>

              {/* Save Outlet Button */}
              <div className="flex justify-end gap-3">
                {outletSaved && (
                  <div className="flex items-center gap-1 text-green-600 text-sm mr-4">
                    <Check size={16} />
                    Settings saved
                  </div>
                )}
                <button
                  onClick={handleSaveOutletSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                  data-testid="save-outlet-settings"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Save Outlet Settings
                </button>
              </div>
            </>
          )}

          {!selectedOutlet && (
            <div className="text-center py-12 text-gray-500">
              <Store size={48} className="mx-auto mb-4 opacity-30" />
              <p>Select an outlet to configure its settings</p>
            </div>
          )}
        </div>
      )}

      {/* Legacy Engine Configuration Section */}
      {activeTab === 'org' && Object.keys(engines).length > 0 && (
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('engines')}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Settings className="text-gray-600" size={20} />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900">Engine Configuration</h2>
                  <p className="text-sm text-gray-500">Legacy settings for 1OS engines</p>
                </div>
              </div>
              {expandedSections.engines ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSections.engines && (
              <div className="border-t border-gray-100">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 p-6">
                  {/* Engine Selector */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold mb-4 text-sm text-gray-600">Engines</h3>
                      <div className="space-y-2">
                        {Object.keys(engines).map((engineName) => (
                          <button
                            key={engineName}
                            onClick={() => setSelectedEngine(engineName)}
                            data-testid={`engine-${engineName}`}
                            className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                              selectedEngine === engineName
                                ? 'bg-white text-blue-700 border border-blue-200 shadow-sm'
                                : 'hover:bg-white text-gray-700'
                            }`}
                          >
                            <span className="text-xl">{getEngineIcon(engineName)}</span>
                            <span className="font-medium text-sm">
                              {engineName.replace(/_/g, ' ')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Configuration Panel */}
                  <div className="lg:col-span-3">
                    {selectedEngine && engines[selectedEngine] && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getEngineIcon(selectedEngine)}</span>
                            <h3 className="font-semibold text-gray-900">
                              {selectedEngine.replace(/_/g, ' ')}
                            </h3>
                          </div>
                          {hasEngineChanges && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditedConfigs({})}
                                data-testid="reset-configs-btn"
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                              >
                                <RotateCcw size={14} />
                                Reset
                              </button>
                              <button
                                onClick={handleSaveEngineConfigs}
                                disabled={saving}
                                data-testid="save-configs-btn"
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                              >
                                <Save size={14} />
                                Save
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="space-y-4">
                          {Object.entries(engines[selectedEngine]).map(([configKey, configInfo]) => (
                            <div
                              key={configKey}
                              data-testid={`config-${configKey}`}
                              className="bg-gray-50 rounded-lg p-4"
                            >
                              <label className="block mb-2">
                                <span className="font-medium text-sm text-gray-900">
                                  {configKey.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                <p className="text-xs text-gray-600 mt-1">{configInfo.description}</p>
                              </label>
                              <input
                                type="text"
                                value={getConfigValue(selectedEngine, configKey)}
                                onChange={(e) =>
                                  handleConfigChange(selectedEngine, configKey, e.target.value)
                                }
                                data-testid={`input-${configKey}`}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Default: {configInfo.default}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Initialize Configs Button (if no configs exist) */}
      {configs.length === 0 && Object.keys(engines).length === 0 && activeTab === 'org' && (
        <div className="mt-6 text-center">
          <button
            onClick={initializeConfigs}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Initialize Engine Configs
          </button>
        </div>
      )}

      {/* Time Integrity Tab */}
      {activeTab === 'time-integrity' && (
        <TimeIntegrityDashboard embedded />
      )}
    </div>
  );
};

export default Configuration;
